/* V218.10 — Exit-intent free SEO audit lead capture for SEO Indexer Connect page.
 *
 * Triggers when:
 *   - Desktop: mouse leaves through the top of the viewport
 *   - Mobile: fast pull-down gesture near the top of the page
 *
 * Cooldown: 7 days (localStorage) — once shown, doesn't re-appear all week.
 * Submit path: POST /api/seo-indexer/free-audit-lead — soft lead with just
 * sitemap URL (we don't even ask for email; lower friction = higher capture).
 */
import { useEffect, useRef, useState } from "react";
import { X, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STORAGE_KEY = "trp_seo_indexer_exit_intent_shown_at";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const ARM_AFTER_MS = 8000; // user must be on page ≥ 8s before we arm

function _alreadyShownRecently() {
  try {
    const ts = Number(localStorage.getItem(STORAGE_KEY));
    return !!ts && Date.now() - ts < COOLDOWN_MS;
  } catch {
    return false;
  }
}

function _markShown() {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
}

export default function SeoIndexerExitIntent() {
  const [show, setShow] = useState(false);
  const [sitemap, setSitemap] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const armedRef = useRef(false);
  const lastTouchY = useRef(0);

  // Arm trigger after dwell time
  useEffect(() => {
    if (_alreadyShownRecently()) return;
    const t = setTimeout(() => { armedRef.current = true; }, ARM_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  const trigger = () => {
    if (!armedRef.current || show || submitted) return;
    if (_alreadyShownRecently()) return;
    setShow(true);
    _markShown();
  };

  // Desktop: mouseleave from top edge
  useEffect(() => {
    const onLeave = (e) => { if (e.clientY <= 5) trigger(); };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile: rapid pull-down near top of page
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    const onTouchStart = (e) => { lastTouchY.current = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      const dy = e.touches[0].clientY - lastTouchY.current;
      if (window.scrollY < 30 && dy > 80) trigger();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc to close
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === "Escape") setShow(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!sitemap.trim()) { setErr("Vlož URL svojho webu alebo sitemap."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/seo-indexer/free-audit-lead`, {
        url_or_sitemap: sitemap.trim(),
        contact_email: email.trim() || null,
        source: "exit_intent_connect_page",
      }, { timeout: 12000 });
      setSubmitted(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Niečo zlyhalo. Skús znova alebo nás kontaktuj.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8 bg-black/85 backdrop-blur-sm animate-in fade-in"
      data-testid="seo-indexer-exit-intent"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-lg bg-[#050816] border border-cyan-500/40 rounded-2xl shadow-2xl shadow-cyan-500/10 overflow-hidden">
        <button
          type="button"
          onClick={() => setShow(false)}
          aria-label="Close"
          data-testid="exit-intent-close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {!submitted ? (
          <div className="p-7 sm:p-9">
            <p className="text-cyan-400 text-[10px] font-mono tracking-[0.3em] uppercase mb-2">
              ⏳ Počkaj sekundu
            </p>
            <h2 className="text-2xl sm:text-3xl font-black leading-tight text-white mb-3">
              Odchádzaš bez SEO auditu?
            </h2>
            <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
              Pošli mi URL svojho webu (alebo sitemap.xml). Náš agent ti do{" "}
              <strong className="text-white">24 hodín</strong> pošle bezplatný report:
              koľko URL-iek máš, koľko z nich Bing/Google v skutočnosti vidí, a ktoré
              chýbajú v indexe. Aj keď sa nezaregistruješ.
            </p>

            <form onSubmit={submit} className="space-y-3" data-testid="exit-intent-form">
              <input
                type="text"
                inputMode="url"
                data-testid="exit-intent-sitemap-input"
                value={sitemap}
                onChange={(e) => setSitemap(e.target.value)}
                placeholder="https://your-shop.com (alebo /sitemap.xml)"
                className="w-full px-4 py-3 bg-black/40 border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                autoFocus
                required
              />
              <input
                type="email"
                data-testid="exit-intent-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="(voliteľné) email — kam poslať audit"
                className="w-full px-4 py-3 bg-black/40 border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-cyan-500 focus:outline-none text-sm"
              />

              {err && (
                <div className="bg-rose-950/40 border border-rose-700/60 text-rose-300 text-xs rounded-lg p-2.5" data-testid="exit-intent-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                data-testid="exit-intent-submit"
                disabled={submitting}
                className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-black font-black uppercase tracking-wider text-sm py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {submitting ? "Posielam..." : "Pošlite mi bezplatný audit"}
              </button>

              <p className="text-zinc-500 text-[11px] text-center pt-2">
                Bez záväzkov. Bez karty. GDPR · EU. Email používame iba na doručenie auditu.
              </p>
            </form>
          </div>
        ) : (
          <div className="p-8 text-center" data-testid="exit-intent-success">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-black text-emerald-300 mb-3">Hotovo ✨</h2>
            <p className="text-zinc-300 mb-6">
              Tvoj web je v queue na audit. Pošleme report do <strong className="text-white">24 hodín</strong>{" "}
              {email ? <>na <strong className="text-cyan-300">{email}</strong>.</> : "len čo doplníš email."}
            </p>
            <button
              type="button"
              onClick={() => setShow(false)}
              data-testid="exit-intent-success-close"
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider"
            >
              Zatvoriť
            </button>
            <p className="text-zinc-500 text-xs mt-6">
              Alebo zaregistruj svoj sitemap teraz a indexácia začne do hodiny —{" "}
              <button
                type="button"
                onClick={() => { setShow(false); document.querySelector("[data-testid='connect-sitemap-input']")?.focus(); }}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                stačí 30 sek
              </button>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
